import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Gift, ArrowClockwise, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function VendorGiftCardsPage() {
  const { user } = useAuth();
  const isCardVendor = user?.role === 'card_vendor';
  // Gift card vendors track only complaints+reversed; Card vendors see ALL their cards (it's their full inventory)
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/vendors/${user.id}/my-vouchers`);
      if (isCardVendor) {
        setVouchers(r.data || []);
      } else {
        // Vendor ko sirf complaint + reversed track karne hain (wahi unki balance ko affect karte hain)
        const tracked = (r.data || []).filter((v) => v.status === 'complaint' || v.status === 'reversed');
        setVouchers(tracked);
      }
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [user?.id, isCardVendor]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    try {
      const r = await api.get(`/vendors/${user.id}/my-vouchers/excel`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `my_vouchers_${Date.now()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Export failed'); }
  };

  let filtered = statusFilter === 'all' ? vouchers : vouchers.filter((v) => v.status === statusFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter((v) => v.code?.toLowerCase().includes(q));
  }
  const totals = filtered.reduce((a, v) => ({ face: a.face + (v.face_value || 0), credit: a.credit + (v.credited_amount || 0) }), { face: 0, credit: 0 });
  const byStatus = vouchers.reduce((a, v) => { a[v.status || 'available'] = (a[v.status || 'available'] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-5" data-testid="vendor-gift-cards-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift size={26} weight="duotone" className={isCardVendor ? 'text-indigo-600' : 'text-amber-600'} />
          {isCardVendor ? 'My Cards' : 'Complaints & Reversals Tracker'}
          <span className="text-sm font-normal text-gray-500 ml-1">({vouchers.length})</span>
        </h1>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={load}><ArrowClockwise size={14} /> Refresh</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={exportExcel} data-testid="export-excel-btn">
            <DownloadSimple size={14} /> Export Excel{isCardVendor ? '' : ' (All)'}
          </Button>
        </div>
      </div>

      {!isCardVendor && (
        <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          ℹ️ Yahan par sirf wahi vouchers dikh rahe hain jin par <b>Complaint raised</b> hui hai ya <b>Reversed</b> ho gaye hain — yani jo aapke ledger balance ko affect karte hain. Poora stock dekhne ke liye Excel Export use karein.
        </div>
      )}

      {/* Summary cards — different for card vendor vs gift card vendor */}
      {isCardVendor ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['available', 'Available', 'emerald'],
            ['assigned', 'Assigned', 'blue'],
            ['sold', 'Sold', 'indigo'],
            ['complaint', 'Complaint', 'amber'],
            ['reversed', 'Reversed', 'red'],
          ].map(([k, label, c]) => (
            <div key={k} className={`bg-${c}-50 border border-${c}-200 rounded-lg p-3`}>
              <div className={`text-[10px] uppercase text-${c}-700 font-semibold`}>{label}</div>
              <div className={`text-xl font-bold text-${c}-900`}>{byStatus[k] || 0}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            ['complaint', 'Under Complaint', 'amber', 'Dispute chal raha hai'],
            ['reversed', 'Reversed', 'red', 'Ledger se debit ho gaya'],
          ].map(([k, label, c, desc]) => (
            <div key={k} className={`bg-${c}-50 border border-${c}-200 rounded-lg p-4`}>
              <div className={`text-xs uppercase text-${c}-700 font-semibold`}>{label}</div>
              <div className={`text-2xl font-bold text-${c}-900 mt-1`}>{byStatus[k] || 0}</div>
              <div className={`text-[10px] text-${c}-600 mt-1`}>{desc}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-lg p-3 flex gap-2 flex-wrap items-center">
        {(isCardVendor ? ['all', 'available', 'assigned', 'sold', 'complaint', 'reversed'] : ['all', 'complaint', 'reversed']).map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs font-semibold rounded border ${statusFilter === s ? (isCardVendor ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-amber-600 border-amber-600 text-white') : 'bg-white border-gray-300 hover:bg-gray-50'}`}
            data-testid={`filter-${s}`}>
            {s.toUpperCase()} {s !== 'all' && byStatus[s] ? `(${byStatus[s]})` : ''}
          </button>
        ))}
        <div className="ml-auto">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Code / vendor search..."
            matchCount={filtered.length}
            totalCount={(filtered || []).length}
            noun="vouchers"
            testId="vendor-gc-search"
            inputClassName="pl-8 pr-7 h-8 text-xs w-56"
          />
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-fuchsia-50 border-b text-xs flex justify-between flex-wrap gap-2">
          <span className="text-fuchsia-900">Showing <b>{filtered.length}</b> vouchers</span>
          <span>Total Face: <b>{fmt(totals.face)}</b> • Credited: <b className="text-emerald-700">{fmt(totals.credit)}</b></span>
        </div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : filtered.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">No vouchers</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Upload Date</th>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">PIN</th>
                  <th className="px-3 py-2 text-right">Face</th>
                  <th className="px-3 py-2 text-right">Credit</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Assigned Staff</th>
                  <th className="px-3 py-2 text-left">Sold To</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v, i) => (
                  <tr key={v.id} className={`border-t hover:bg-gray-50 ${v.has_complaint ? 'bg-amber-50/60' : ''}`} data-testid={`gc-row-${v.id}`}>
                    <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-1.5 text-xs">{fmtDate(v.created_at)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{v.code}</td>
                    <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{v.pin || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{fmt(v.face_value)}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-700">{fmt(v.credited_amount)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        v.status === 'available' ? 'bg-emerald-100 text-emerald-700' :
                        v.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                        v.status === 'sold' ? 'bg-indigo-100 text-indigo-700' :
                        v.status === 'complaint' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>{v.status?.toUpperCase()}</span>
                    </td>
                    <td className="px-3 py-1.5 text-xs">{v.assigned_to_staff_name || '—'}</td>
                    <td className="px-3 py-1.5 text-xs">{v.sold_to_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
